/* REACT */
import { ReactNode, useEffect, useState } from "react";
/* HOOKS */
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
/* STORE */
import { useAuthStore } from "@/store/authStore";
/* COMPONENTS */
import { TopNav } from "./TopNav";
import { Sidebar } from "./Sidebar";
import { FeedbackButton } from "@/features/feedback";
/* LIB */
import { isFeedbackEnabled } from "@/lib/env";

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * AppLayout component
 * @param children - The children to render
 * @returns The AppLayout component
 * Last updated: July 16, 2026
 * Updated by: Jette
 */
export const AppLayout = ({ children }: AppLayoutProps) => {
  // Update browser tab title based on current route
  useDocumentTitle();

  const kioskMode = useAuthStore((state) => state.kioskMode);

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const saved = localStorage.getItem("sidebar-collapsed");
    if (saved !== null) {
      return saved === "true";
    }
    const isMobileScreen = window.innerWidth < 600;
    return isMobileScreen;
  });

  useEffect(() => {
    const handleToggle = (event: Event) => {
      const detail = (event as CustomEvent<{ collapsed: boolean }>).detail;
      if (typeof detail?.collapsed === "boolean") {
        setSidebarCollapsed(detail.collapsed);
      }
    };

    window.addEventListener("sidebar-toggle", handleToggle);
    return () => window.removeEventListener("sidebar-toggle", handleToggle);
  }, []);

  if(kioskMode){
    return (
      <div className="min-h-screen bg-background">
        <TopNav contentPaddingClasses="px-4 sm:px-6" />
        <main className="px-4 sm:px-6 py-6">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav
        contentPaddingClasses={`pl-6 ${sidebarCollapsed ? "lg:pl-20" : "lg:pl-60"} pr-4 sm:pr-6 transition-all duration-300`}
      />
      <Sidebar />
      <main
        className={`${
          sidebarCollapsed ? "ml-16" : "ml-56"
        } px-4 sm:px-6 py-6 transition-all duration-300`}
      >
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>
      {/* Floating feedback button — authenticated pages only, hidden in production */}
      {isFeedbackEnabled && <FeedbackButton />}
    </div>
  );
};
